import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { paymentAPI, analysisAPI, getUrlParameter } from '../services/api';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // 'checking', 'success', 'processing', 'error'
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState('');
  const [analysisId, setAnalysisId] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id') || getUrlParameter('session_id');
    if (!sessionId) {
      setStatus('error');
      setError('Invalid payment session. No session ID provided.');
      return;
    }

    checkPaymentStatus(sessionId);
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus('error');
      setError('Payment verification timed out. Please check your dashboard or contact support.');
      return;
    }

    try {
      const data = await paymentAPI.getCheckoutStatus(sessionId);
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        
        // Extract analysis ID from metadata
        const metadata = data.metadata || {};
        const analysisIdFromMeta = metadata.analysis_id;
        
        if (analysisIdFromMeta) {
          setAnalysisId(analysisIdFromMeta);
          
          // Trigger analysis processing
          try {
            await analysisAPI.triggerProcessing(analysisIdFromMeta);
            setStatus('processing');
          } catch (processingError) {
            console.error('Failed to trigger processing:', processingError);
            // Still show success since payment went through
            setStatus('success');
          }
        }
        
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        setError('Payment session expired. Please try again.');
        return;
      } else if (data.payment_status === 'unpaid') {
        // Continue polling
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
        return;
      }

      // If we get here, continue polling
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setStatus('error');
      setError('Error verifying payment. Please check your dashboard or contact support.');
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'checking':
        return <Loader2 className="h-16 w-16 text-blue-600 animate-spin" />;
      case 'success':
        return <CheckCircle className="h-16 w-16 text-green-600" />;
      case 'processing':
        return <Loader2 className="h-16 w-16 text-purple-600 animate-spin" />;
      case 'error':
        return <AlertCircle className="h-16 w-16 text-red-600" />;
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'checking':
        return 'Verifying Payment...';
      case 'success':
        return 'Payment Successful!';
      case 'processing':
        return 'Analysis Starting...';
      case 'error':
        return 'Payment Verification Failed';
      default:
        return '';
    }
  };

  const getDescription = () => {
    switch (status) {
      case 'checking':
        return 'Please wait while we verify your payment with Stripe...';
      case 'success':
        return 'Your payment has been successfully processed. You can now access your analysis results.';
      case 'processing':
        return 'Your payment is confirmed and we\'re now processing your resume analysis. This may take a few minutes.';
      case 'error':
        return error;
      default:
        return '';
    }
  };

  const getAmountDisplay = () => {
    if (!paymentData) return null;

    const amount = paymentData.amount_total ? (paymentData.amount_total / 100).toFixed(2) : '0.00';
    const currency = paymentData.currency?.toUpperCase() || 'USD';

    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <div className="text-center">
          <p className="text-sm text-green-800 mb-1">Amount Paid</p>
          <p className="text-2xl font-bold text-green-900">
            {currency} ${amount}
          </p>
        </div>
      </div>
    );
  };

  const handleViewDashboard = () => {
    navigate('/dashboard');
  };

  const handleViewAnalysis = () => {
    if (analysisId) {
      navigate(`/analysis/${analysisId}`);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto flex items-center justify-center mb-4">
            {getIcon()}
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            {getTitle()}
          </CardTitle>
          <CardDescription className="mt-2 text-base">
            {getDescription()}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {getAmountDisplay()}
          
          {status === 'success' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 text-center">
                Your resume analysis is now available in your dashboard. 
                {analysisId && ' You can view the results or track processing progress.'}
              </p>
            </div>
          )}

          {status === 'processing' && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-800 text-center">
                We're analyzing your resume with AI. You'll receive detailed insights including:
              </p>
              <ul className="text-xs text-purple-700 mt-2 space-y-1">
                <li>• Match score and analysis</li>
                <li>• ATS compliance check</li>
                <li>• Specific recommendations</li>
                <li>• Cover letter (if included in your package)</li>
              </ul>
            </div>
          )}

          <div className="flex flex-col space-y-2">
            {(status === 'success' || status === 'processing') && analysisId && (
              <Button onClick={handleViewAnalysis} className="w-full">
                <ArrowRight className="h-4 w-4 mr-2" />
                View Analysis
              </Button>
            )}
            
            <Button 
              variant={status === 'error' ? 'default' : 'outline'}
              onClick={handleViewDashboard}
              className="w-full"
            >
              Go to Dashboard
            </Button>
          </div>

          {status === 'error' && (
            <div className="text-center">
              <p className="text-sm text-gray-600">
                If you believe this is an error, please contact support with session ID:
              </p>
              <p className="text-xs text-gray-500 font-mono mt-1">
                {searchParams.get('session_id') || 'N/A'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;